import { Component } from "./component-base.js";
import { ThemeBus } from "./events-bus.js";
import { EVENTS } from "./event-constants.js";
import { initHeaderOverlayController } from "./header-overlay-controller.js";
import { initDrawerOverlayController } from "./drawer-overlay-controller.js";
import { QuantitySelectorComponent } from "./quantity-selector.js";
import { updateCartBubble } from "./utilities.js";
import "./lazy-image.js";

// Cart Drawer Component sitewide event

class CartDrawerComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.initialized) return;
    this.initialized = true;

    // Prevent initial flicker while syncing state
    const prevVisibility = this.style.visibility;
    this.style.visibility = 'hidden';

    this.overlay = document.querySelector("[data-site-overlay]");
    this.toggleButtons = document.querySelectorAll("[data-drawer-toggle]");
    this.closeButtons = this.querySelectorAll("[data-drawer-close]");
    // Default to closed unless explicitly open
    this.isOpen = this.classList.contains("translate-x-0");
    this.removeButtons = document.querySelectorAll("[data-cart-remove]");

    this.#syncOverlayState();
    this.#addEventListeners();
    this.#attachRemoveListeners();
    this.#attachCloseListeners();

    // Ensure overlay reflects open state without direct DOM manipulation
    if (this.isOpen) {
      ThemeBus.dispatchEvent(new Event(EVENTS.drawerOverlayShow));
    }

    // Reveal after sync and mark as synced to avoid first-frame animations
    requestAnimationFrame(() => {
      this.dataset.synced = 'true';
      this.style.visibility = prevVisibility;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeEventListeners();
  }

  #addEventListeners() {
    ThemeBus.addEventListener(EVENTS.cartDrawerOpen, this.#handleOpen);
    ThemeBus.addEventListener(EVENTS.cartDrawerClose, this.#handleClose);
    ThemeBus.addEventListener(EVENTS.cartDrawerToggle, this.#handleToggle);
    ThemeBus.addEventListener(EVENTS.cartItemAdded, this.#handleItemAdded);
    ThemeBus.addEventListener(EVENTS.cartUpdated, this.#handleCartUpdated);

    this.toggleButtons.forEach((btn) =>
      btn.addEventListener("click", this.#handleToggle)
    );

    if (this.overlay) {
      this.overlay.addEventListener("click", this.#handleClose);
    }
  }

  #attachCloseListeners() {
    const closeButtons = this.querySelectorAll("[data-drawer-close]");
    closeButtons.forEach((button) => {
      button.addEventListener("click", this.#handleClose);
    });
  }

  #attachRemoveListeners() {
    const removeButtons = this.querySelectorAll("[data-cart-remove]");

    removeButtons.forEach((button) => {
      button.addEventListener("click", async () => {
        const line = Number(button.dataset.line);
        if (!line) return;

        try {
          const res = await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ line, quantity: 0 }),
          });

          if (!res.ok) throw new Error("Remove failed");
          const updatedCart = await res.json();

          window.__cart = updatedCart;
          ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdated));
        } catch (err) {
          if (document.body.dataset.environment === 'development') {
            console.error("Error removing item from cart:", err);
          }
        }
      });
    });
  }

  #handleCartUpdated = async () => {
    await this.#renderCartContents();
    updateCartBubble();
  };

  async #renderCartContents() {
    try {
      // Use the cart.js endpoint to get the latest cart data
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      
      // Store the latest cart data
      window.__cart = cartData;
      
      // Update cart count attribute
      this.dataset.cartCount = cartData.item_count;
      
      // Show/hide header and footer based on cart count
      const headerWrapper = this.querySelector('.cart-drawer-header-wrapper');
      const footerWrapper = this.querySelector('.cart-drawer-footer-wrapper');
      
      if (cartData.item_count === 0) {
        // Add transition classes for smooth fade out
        if (headerWrapper) {
          headerWrapper.style.transition = 'opacity 300ms ease-out';
          headerWrapper.style.opacity = '0';
        }
        if (footerWrapper) {
          footerWrapper.style.transition = 'opacity 300ms ease-out';
          footerWrapper.style.opacity = '0';
        }
        // Hide after fade out completes (with slight extra delay)
        setTimeout(() => {
          if (headerWrapper) headerWrapper.classList.add('hidden');
          if (footerWrapper) footerWrapper.classList.add('hidden');
        }, 400);
      } else {
        // Show immediately when adding items
        if (headerWrapper) {
          headerWrapper.classList.remove('hidden');
          headerWrapper.style.opacity = '0';
          // Force reflow
          headerWrapper.offsetHeight;
          headerWrapper.style.transition = 'opacity 300ms ease-in';
          headerWrapper.style.opacity = '1';
        }
        if (footerWrapper) {
          footerWrapper.classList.remove('hidden');
          footerWrapper.style.opacity = '0';
          // Force reflow
          footerWrapper.offsetHeight;
          footerWrapper.style.transition = 'opacity 300ms ease-in';
          footerWrapper.style.opacity = '1';
        }
      }
      
      // Fetch the rendered cart drawer section
      const response = await fetch(`/?section_id=cart-drawer&v=${Date.now()}`, { cache: 'no-store' });
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, "text/html");
      
      // Find the section content (Shopify wraps sections in a div)
      const sectionContent = html.querySelector('.shopify-section') || html.body;
      
      // Update header (including cart count/shipping bar)
      const newHeader = sectionContent.querySelector("cart-drawer-header");
      const currentHeader = this.querySelector("cart-drawer-header");

      if (newHeader && currentHeader) {
        currentHeader.innerHTML = newHeader.innerHTML;
        this.#attachCloseListeners();
      }
      
      // Update cart items
      const newContent = sectionContent.querySelector("[data-cart-drawer-contents]");
      const currentContainer = this.querySelector("[data-cart-drawer-contents]");

      if (newContent && currentContainer) {
        currentContainer.innerHTML = newContent.innerHTML;
        this.#attachRemoveListeners();
        this.#attachCloseListeners();
        
        // If cart is now empty, hide the empty state initially and fade it in after header/footer are gone
        if (cartData.item_count === 0) {
          const emptyState = currentContainer.querySelector('.flex.flex-col.items-center.justify-center');
          if (emptyState) {
            emptyState.style.opacity = '0';
            emptyState.style.transition = 'opacity 300ms ease-in';
            // Fade in after header/footer are hidden
            setTimeout(() => {
              emptyState.style.opacity = '1';
            }, 400);
          }
        }
      } else {
        if (document.body.dataset.environment === 'development') {
          console.warn('[CartDrawer] Could not find cart contents elements');
        }
      }

      // Update total price
      const newTotalPrice = sectionContent.querySelector("[data-cart-total-price]");
      const currentTotalPrice = this.querySelector("[data-cart-total-price]");

      if (newTotalPrice && currentTotalPrice) {
        currentTotalPrice.textContent = newTotalPrice.textContent;
      } else {
        if (document.body.dataset.environment === 'development') {
          console.warn('[CartDrawer] Could not find total price elements', {
            newTotalPrice: !!newTotalPrice,
            currentTotalPrice: !!currentTotalPrice
          });
        }
      }

      // Update entire footer to catch discount changes
      const newFooter = sectionContent.querySelector("cart-drawer-footer");
      const currentFooter = this.querySelector("cart-drawer-footer");

      if (newFooter && currentFooter) {
        currentFooter.innerHTML = newFooter.innerHTML;
      }
    } catch (error) {
      console.error("Error rendering cart contents:", error);
    }
  }

  #removeEventListeners() {
    ThemeBus.removeEventListener(EVENTS.cartDrawerOpen, this.#handleOpen);
    ThemeBus.removeEventListener(EVENTS.cartDrawerClose, this.#handleClose);
    ThemeBus.removeEventListener(EVENTS.cartDrawerToggle, this.#handleToggle);
    ThemeBus.removeEventListener(EVENTS.cartItemAdded, this.#handleItemAdded);
    ThemeBus.removeEventListener(EVENTS.cartUpdated, this.#handleCartUpdated);

    this.toggleButtons.forEach((btn) =>
      btn.removeEventListener("click", this.#handleToggle)
    );

    if (this.overlay) {
      this.overlay.removeEventListener("click", this.#handleClose);
    }
  }

  #handleOpen = () => {
    this.isOpen = true;
    this.classList.remove("translate-x-full");
    this.classList.add("translate-x-0");
    document.body.classList.add("overflow-hidden");
    // Also show drawer overlay via event bus
    ThemeBus.dispatchEvent(new Event(EVENTS.drawerOverlayShow));
  };

  #handleClose = () => {
    this.isOpen = false;
    
    // Remove focus from any focused element inside the drawer
    if (this.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    
    this.classList.remove("translate-x-0");
    this.classList.add("translate-x-full");
    document.body.classList.remove("overflow-hidden");
    // Also hide drawer overlay via event bus
    ThemeBus.dispatchEvent(new Event(EVENTS.drawerOverlayHide));
  };

  #handleToggle = () => {
    this.isOpen ? this.#handleClose() : this.#handleOpen();
  };

  #handleItemAdded = async () => {
    await this.#renderCartContents();
    this.#handleOpen();
    updateCartBubble();
  };

  #syncOverlayState() {
    // Avoid animating on initial sync
    const prevTransition = this.style.transition;
    this.style.transition = 'none';
    if (this.isOpen) {
      this.classList.add("translate-x-0");
      this.classList.remove("translate-x-full");
    } else {
      this.classList.remove("translate-x-0");
      this.classList.add("translate-x-full");
    }
    // Force reflow then restore transitions
    void this.offsetHeight;
    this.style.transition = prevTransition;
  }
}

class HeaderDrawerComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.initialized) return;
    this.initialized = true;

    this.overlay = document.querySelector('[data-site-overlay]');
    this.toggleButtons = document.querySelectorAll('[data-header-drawer-toggle]');
    
    // Ensure drawer starts in closed state
    this.isOpen = this.classList.contains('header-drawer-open');
    
    // Force closed state if not explicitly open
    if (!this.isOpen) {
      this.classList.add('header-drawer-closed');
      this.classList.remove('header-drawer-open');
      this.setAttribute('aria-hidden', 'true');
      this.#disableFocusableElements();
    } else {
      this.#enableFocusableElements();
    }

    this.#addEventListeners();

    // Ensure overlay reflects open state
    if (this.isOpen) {
      ThemeBus.dispatchEvent(new Event(EVENTS.drawerOverlayShow));
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeEventListeners();
  }

  #addEventListeners() {
    this.toggleButtons.forEach(btn =>
      btn.addEventListener('click', this.#handleToggle)
    );

    ThemeBus.addEventListener(EVENTS.headerDrawerOpen, this.#handleOpen);
    ThemeBus.addEventListener(EVENTS.headerDrawerClose, this.#handleClose);
    ThemeBus.addEventListener(EVENTS.headerDrawerToggle, this.#handleToggle);

    this.overlay?.addEventListener('click', this.#handleClose);
  }

  #removeEventListeners() {
    this.toggleButtons.forEach(btn =>
      btn.removeEventListener('click', this.#handleToggle)
    );

    ThemeBus.removeEventListener(EVENTS.headerDrawerOpen, this.#handleOpen);
    ThemeBus.removeEventListener(EVENTS.headerDrawerClose, this.#handleClose);
    ThemeBus.removeEventListener(EVENTS.headerDrawerToggle, this.#handleToggle);

    this.overlay?.removeEventListener('click', this.#handleClose);
  }

  #handleOpen = () => {
    this.isOpen = true;
    this.classList.remove('header-drawer-closed');
    this.classList.add('header-drawer-open');
    this.setAttribute('aria-hidden', 'false');
    this.#enableFocusableElements();
    document.body.classList.add('overflow-hidden');
    // Show drawer overlay via event bus
    ThemeBus.dispatchEvent(new Event(EVENTS.drawerOverlayShow));
  }

  #handleClose = () => {
    this.isOpen = false;
    
    // Remove focus from any focused element inside the drawer
    if (this.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    
    this.classList.remove('header-drawer-open');
    this.classList.add('header-drawer-closed');
    this.setAttribute('aria-hidden', 'true');
    this.#disableFocusableElements();
    document.body.classList.remove('overflow-hidden');
    // Hide drawer overlay via event bus
    ThemeBus.dispatchEvent(new Event(EVENTS.drawerOverlayHide));
  }

  /**
   * Disables focusable elements when drawer is hidden
   */
  #disableFocusableElements() {
    const focusableElements = this.querySelectorAll(
      'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
    );
    focusableElements.forEach(el => {
      el.setAttribute('tabindex', '-1');
    });
  }

  /**
   * Re-enables focusable elements when drawer is shown
   */
  #enableFocusableElements() {
    const focusableElements = this.querySelectorAll(
      'a[href], button, input, textarea, select, details, [tabindex="-1"]'
    );
    focusableElements.forEach(el => {
      el.removeAttribute('tabindex');
    });
  }

  #handleToggle = () => {
    this.isOpen ? this.#handleClose() : this.#handleOpen();
  }
}

/**
 * HeaderDrawerMenu Component
 * 
 * Handles multi-level navigation with slide-in transitions
 * - Manages panel visibility and transitions for submenus
 * - Syncs with main drawer open/close state via MutationObserver
 * - Resets to main menu when drawer closes
 * - Uses ThemeBus for accessibility announcements
 */
class HeaderDrawerMenu extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.initialized) return;
    this.initialized = true;

    // State
    this.currentLevel = 'main'; // main, submenu, or grandchild
    this.activeSubmenuIndex = null;
    this.activeGrandchildIndex = null;

    // Elements
    this.mainPanel = this.querySelector('[data-menu-level="main"]');
    this.submenuPanels = this.querySelectorAll('[data-menu-level="submenu"]');
    this.grandchildPanels = this.querySelectorAll('[data-menu-level="grandchild"]');
    this.submenuTriggers = this.querySelectorAll('[data-submenu-trigger]');
    this.grandchildTriggers = this.querySelectorAll('[data-grandchild-trigger]');
    this.submenuBackButtons = this.querySelectorAll('[data-submenu-back]');
    this.grandchildBackButtons = this.querySelectorAll('[data-grandchild-back]');

    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  #init() {
    // Ensure all panels start in correct position
    this.#initializePanelPositions();

    // Bind submenu triggers
    this.submenuTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => this.#openSubmenu(e));
    });

    // Bind grandchild triggers
    this.grandchildTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => this.#openGrandchild(e));
    });

    // Bind submenu back buttons
    this.submenuBackButtons.forEach((button) => {
      button.addEventListener('click', (e) => this.#closeSubmenu(e));
    });

    // Bind grandchild back buttons
    this.grandchildBackButtons.forEach((button) => {
      button.addEventListener('click', (e) => this.#closeGrandchild(e));
    });

    // Bind all menu links to close drawer when clicked
    this.#attachLinkListeners();

    // Watch for drawer closing to reset menu
    // Use MutationObserver to detect when parent drawer closes
    const drawerComponent = this.closest('header-drawer-component');
    if (drawerComponent) {
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'aria-hidden') {
            const isHidden = drawerComponent.getAttribute('aria-hidden') === 'true';
            if (isHidden) {
              this.#resetMenu();
            }
          }
        });
      });

      this.observer.observe(drawerComponent, {
        attributes: true,
        attributeFilter: ['aria-hidden'],
      });
    }
  }

  /**
   * Attaches click listeners to all menu links to close drawer
   */
  #attachLinkListeners() {
    const allLinks = this.querySelectorAll('a[href]:not([href="#"])');
    allLinks.forEach((link) => {
      link.addEventListener('click', () => {
        // Close the drawer via ThemeBus
        try {
          ThemeBus.dispatchEvent(new Event(EVENTS.headerDrawerClose));
        } catch (_) {}
      });
    });
  }

  /**
   * Ensures all panels are in correct initial position
   */
  #initializePanelPositions() {
    // Main panel should be visible and centered
    if (this.mainPanel) {
      this.mainPanel.classList.add('translate-x-0');
      this.mainPanel.classList.remove('-translate-x-full');
      this.mainPanel.setAttribute('aria-hidden', 'false');
    }

    // All submenu panels should be off-screen to the right
    this.submenuPanels.forEach((panel) => {
      panel.classList.add('translate-x-full');
      panel.classList.remove('translate-x-0', '-translate-x-full');
      panel.setAttribute('aria-hidden', 'true');
    });

    // All grandchild panels should be off-screen to the right
    this.grandchildPanels.forEach((panel) => {
      panel.classList.add('translate-x-full');
      panel.classList.remove('translate-x-0', '-translate-x-full');
      panel.setAttribute('aria-hidden', 'true');
    });

    // All triggers should be collapsed
    this.submenuTriggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });
    this.grandchildTriggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  /**
   * Opens a submenu panel with slide-in animation
   */
  #openSubmenu(event) {
    const trigger = event.currentTarget;
    const submenuIndex = trigger.dataset.submenuTrigger;
    const submenuPanel = this.querySelector(`#submenu-${submenuIndex}`);

    if (!submenuPanel) return;

    // Update state
    this.currentLevel = 'submenu';
    this.activeSubmenuIndex = submenuIndex;

    // Update ARIA
    trigger.setAttribute('aria-expanded', 'true');
    submenuPanel.setAttribute('aria-hidden', 'false');

    // Animate: slide main panel left, slide submenu in from right
    this.mainPanel.classList.remove('translate-x-0');
    this.mainPanel.classList.add('-translate-x-full');

    submenuPanel.classList.remove('translate-x-full');
    submenuPanel.classList.add('translate-x-0');

    // Announce to screen readers
    this.#announceNavigation(`Viewing ${trigger.textContent.trim()} menu`);
  }

  /**
   * Closes active submenu and returns to main menu
   */
  #closeSubmenu(event) {
    const button = event.currentTarget;
    const submenuIndex = button.dataset.submenuBack;
    const submenuPanel = this.querySelector(`#submenu-${submenuIndex}`);
    const trigger = this.querySelector(`[data-submenu-trigger="${submenuIndex}"]`);

    if (!submenuPanel) return;

    // Update state
    this.currentLevel = 'main';
    this.activeSubmenuIndex = null;

    // Update ARIA
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    submenuPanel.setAttribute('aria-hidden', 'true');

    // Animate: slide submenu right, slide main panel back to center
    submenuPanel.classList.remove('translate-x-0');
    submenuPanel.classList.add('translate-x-full');

    this.mainPanel.classList.remove('-translate-x-full');
    this.mainPanel.classList.add('translate-x-0');

    // Announce to screen readers
    this.#announceNavigation('Back to main menu');
  }

  /**
   * Opens a grandchild panel with slide-in animation
   */
  #openGrandchild(event) {
    const trigger = event.currentTarget;
    const grandchildIndex = trigger.dataset.grandchildTrigger;
    const grandchildPanel = this.querySelector(`#grandchild-${grandchildIndex}`);

    if (!grandchildPanel) return;

    // Update state
    this.currentLevel = 'grandchild';
    this.activeGrandchildIndex = grandchildIndex;

    // Update ARIA
    trigger.setAttribute('aria-expanded', 'true');
    grandchildPanel.setAttribute('aria-hidden', 'false');

    // Find the current submenu panel
    const [submenuIndex] = grandchildIndex.split('-');
    const submenuPanel = this.querySelector(`#submenu-${submenuIndex}`);

    // Animate: slide submenu left, slide grandchild in from right
    if (submenuPanel) {
      submenuPanel.classList.remove('translate-x-0');
      submenuPanel.classList.add('-translate-x-full');
    }

    grandchildPanel.classList.remove('translate-x-full');
    grandchildPanel.classList.add('translate-x-0');

    // Announce to screen readers
    this.#announceNavigation(`Viewing ${trigger.textContent.trim()} menu`);
  }

  /**
   * Closes active grandchild and returns to submenu
   */
  #closeGrandchild(event) {
    const button = event.currentTarget;
    const grandchildIndex = button.dataset.grandchildBack;
    const grandchildPanel = this.querySelector(`#grandchild-${grandchildIndex}`);
    const trigger = this.querySelector(`[data-grandchild-trigger="${grandchildIndex}"]`);

    if (!grandchildPanel) return;

    // Update state
    this.currentLevel = 'submenu';
    this.activeGrandchildIndex = null;

    // Update ARIA
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    grandchildPanel.setAttribute('aria-hidden', 'true');

    // Find the parent submenu panel
    const [submenuIndex] = grandchildIndex.split('-');
    const submenuPanel = this.querySelector(`#submenu-${submenuIndex}`);

    // Animate: slide grandchild right, slide submenu back to center
    grandchildPanel.classList.remove('translate-x-0');
    grandchildPanel.classList.add('translate-x-full');

    if (submenuPanel) {
      submenuPanel.classList.remove('-translate-x-full');
      submenuPanel.classList.add('translate-x-0');
    }

    // Announce to screen readers
    this.#announceNavigation('Back to submenu');
  }

  /**
   * Resets menu to initial state (called when drawer closes)
   */
  #resetMenu() {
    // Return to main panel immediately (no animation since drawer is closed)
    if (this.currentLevel !== 'main') {
      // Reset all panels
      this.mainPanel.classList.remove('-translate-x-full');
      this.mainPanel.classList.add('translate-x-0');

      this.submenuPanels.forEach((panel) => {
        panel.classList.remove('translate-x-0', '-translate-x-full');
        panel.classList.add('translate-x-full');
        panel.setAttribute('aria-hidden', 'true');
      });

      this.grandchildPanels.forEach((panel) => {
        panel.classList.remove('translate-x-0', '-translate-x-full');
        panel.classList.add('translate-x-full');
        panel.setAttribute('aria-hidden', 'true');
      });

      // Reset triggers
      this.submenuTriggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });
      
      this.grandchildTriggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });

      // Reset state
      this.currentLevel = 'main';
      this.activeSubmenuIndex = null;
      this.activeGrandchildIndex = null;
    }

    // Scroll main panel back to top
    const mainPanelScroller = this.mainPanel?.querySelector('.overflow-y-auto');
    if (mainPanelScroller) {
      mainPanelScroller.scrollTop = 0;
    }
  }

  /**
   * Announces navigation changes to screen readers
   */
  #announceNavigation(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.className = 'sr-only';
    announcement.textContent = message;

    this.appendChild(announcement);

    // Remove after announcement
    setTimeout(() => {
      announcement.remove();
    }, 1000);
  }
}

class SearchDrawerComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.initialized) return;
    this.initialized = true;

    this.overlay = document.querySelector('[data-site-overlay]');
    this.closeButton = this.querySelector('[data-search-drawer-toggle]');
    this.searchInput = this.querySelector('input[type="search"]');
    
    // Ensure drawer starts in closed state
    this.isOpen = this.classList.contains('translate-x-0');
    
    // Force closed state if not explicitly open
    if (!this.isOpen) {
      this.classList.add('-translate-x-full');
      this.classList.remove('translate-x-0');
      this.setAttribute('aria-hidden', 'true');
      this.#disableFocusableElements();
    } else {
      this.#enableFocusableElements();
    }

    this.#addEventListeners();

    // Ensure overlay reflects open state
    if (this.isOpen) {
      ThemeBus.dispatchEvent(new Event(EVENTS.drawerOverlayShow));
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeEventListeners();
  }

  #addEventListeners() {
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.#handleClose);
    }

    ThemeBus.addEventListener(EVENTS.searchDrawerOpen, this.#handleOpen);
    ThemeBus.addEventListener(EVENTS.searchDrawerClose, this.#handleClose);
    ThemeBus.addEventListener(EVENTS.searchDrawerToggle, this.#handleToggle);

    this.overlay?.addEventListener('click', this.#handleClose);
  }

  #removeEventListeners() {
    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.#handleClose);
    }

    ThemeBus.removeEventListener(EVENTS.searchDrawerOpen, this.#handleOpen);
    ThemeBus.removeEventListener(EVENTS.searchDrawerClose, this.#handleClose);
    ThemeBus.removeEventListener(EVENTS.searchDrawerToggle, this.#handleToggle);

    this.overlay?.removeEventListener('click', this.#handleClose);
  }

  #handleOpen = () => {
    this.isOpen = true;
    this.classList.remove('-translate-x-full');
    this.classList.add('translate-x-0');
    this.setAttribute('aria-hidden', 'false');
    this.#enableFocusableElements();
    document.body.classList.add('overflow-hidden');
    // Show drawer overlay via event bus
    ThemeBus.dispatchEvent(new Event(EVENTS.drawerOverlayShow));

    setTimeout(() => {
      const searchInput = this.querySelector('input[type="search"]');
      if (searchInput) {
        searchInput.focus();
      }
    }, 100);
  }

  #handleClose = () => {
    this.isOpen = false;
    
    // Remove focus from any focused element inside the drawer
    if (this.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    
    this.classList.remove('translate-x-0');
    this.classList.add('-translate-x-full');
    this.setAttribute('aria-hidden', 'true');
    this.#disableFocusableElements();
    document.body.classList.remove('overflow-hidden');
    // Hide drawer overlay via event bus
    ThemeBus.dispatchEvent(new Event(EVENTS.drawerOverlayHide));
  }

  /**
   * Disables focusable elements when drawer is hidden
   */
  #disableFocusableElements() {
    const focusableElements = this.querySelectorAll(
      'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
    );
    focusableElements.forEach(el => {
      el.setAttribute('tabindex', '-1');
    });
  }

  /**
   * Re-enables focusable elements when drawer is shown
   */
  #enableFocusableElements() {
    const focusableElements = this.querySelectorAll(
      'a[href], button, input, textarea, select, details, [tabindex="-1"]'
    );
    focusableElements.forEach(el => {
      el.removeAttribute('tabindex');
    });
  }

  #handleToggle = () => {
    this.isOpen ? this.#handleClose() : this.#handleOpen();
  }
}

class MobileFilterDrawerComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.initialized) return;
    this.initialized = true;

    // Prevent initial flicker while syncing state
    const prevVisibility = this.style.visibility;
    this.style.visibility = 'hidden';

    this.overlay = document.querySelector('[data-site-overlay]');
    this.toggleButtons = document.querySelectorAll('[data-mobile-filter-drawer-toggle]');
    // Default to closed unless explicitly open
    this.isOpen = this.classList.contains('translate-x-0');

    this.#syncOverlayState();
    this.#addEventListeners();
    // Reveal after sync and mark as synced
    requestAnimationFrame(() => {
      this.dataset.synced = 'true';
      this.style.visibility = prevVisibility;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeEventListeners();
  }

  #addEventListeners() {
    this.toggleButtons.forEach(button => {
      button.addEventListener('click', this.#handleToggle);
    });

    this.overlay?.addEventListener('click', this.#handleClose);
    
    // Add clear filters functionality
    const clearButton = document.getElementById('clear-filters');
    if (clearButton) {
      clearButton.addEventListener('click', this.#handleClearFilters);
    }
    
    // Handle explicit Apply button (type="button") for mobile filters
    this.applyButton = this.querySelector('button[form="mobile-filter-form"]');
    if (this.applyButton) {
      this.applyButton.addEventListener('click', this.#handleApplyClick);
    }
    
    // Handle form submission to prevent page reload
    const mobileForm = document.getElementById('mobile-filter-form');
    if (mobileForm) {
      mobileForm.addEventListener('submit', this.#handleFormSubmit);
    }
  }

  #removeEventListeners() {
    this.toggleButtons.forEach(button => {
      button.removeEventListener('click', this.#handleToggle);
    });

    this.overlay?.removeEventListener('click', this.#handleClose);
    
    const clearButton = document.getElementById('clear-filters');
    if (clearButton) {
      clearButton.removeEventListener('click', this.#handleClearFilters);
    }
    if (this.applyButton) {
      this.applyButton.removeEventListener('click', this.#handleApplyClick);
    }
    
    const mobileForm = document.getElementById('mobile-filter-form');
    if (mobileForm) {
      mobileForm.removeEventListener('submit', this.#handleFormSubmit);
    }
  }

  #handleOpen = () => {
    this.isOpen = true;
    this.classList.remove('translate-x-full');
    this.classList.add('translate-x-0');
    document.body.classList.add('overflow-hidden');
    // Also show header overlay via event bus
    ThemeBus.dispatchEvent(new Event(EVENTS.drawerOverlayShow));
  }

  #handleClose = () => {
    this.isOpen = false;
    
    // Remove focus from any focused element inside the drawer
    if (this.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    
    this.classList.remove('translate-x-0');
    this.classList.add('translate-x-full');
    document.body.classList.remove('overflow-hidden');
    // Also hide header overlay via event bus
    ThemeBus.dispatchEvent(new Event(EVENTS.drawerOverlayHide));
  }

  #handleToggle = () => {
    this.isOpen ? this.#handleClose() : this.#handleOpen();
  }

  #handleFormSubmit = (e) => {
    e.preventDefault();
    
    // Apply the mobile filters
    this.#applyMobileFilters();
  }

  #handleApplyClick = (e) => {
    e.preventDefault();
    this.#applyMobileFilters();
  }

  #handleClearFilters = () => {
    const form = document.getElementById('mobile-filter-form');
    if (form) {
      // Check if we're on a faceted sub-collection - if so, navigate to parent
      const facetedFilterOption = form.querySelector('[data-faceted-active-filter-option]') || document.querySelector('[data-faceted-active-filter-option]');
      
      if (facetedFilterOption) {
        const facetedFilterLink = facetedFilterOption.closest('a[data-faceted-filter-link]') || document.querySelector('a[data-faceted-active-filter]');
        const parentHref = facetedFilterLink?.getAttribute('href') || facetedFilterLink?.getAttribute('data-faceted-filter-link');

        if (parentHref) {
          // Navigate to parent collection (clears faceted filter)
          const parentUrl = new URL(parentHref, window.location.origin);
          window.location.href = parentUrl.pathname;
          return;
        }
      }
      
      // Uncheck all filter checkboxes (both mobile and desktop) and update visual styles
      document.querySelectorAll('input[type="checkbox"][name^="filter."]').forEach(checkbox => {
        checkbox.checked = false;
        
        // Update visual checkbox styles
        const label = checkbox.closest('label');
        if (label) {
          const visualCheckbox = label.querySelector('span.h-4.w-4');
          if (visualCheckbox) {
            visualCheckbox.classList.remove('bg-input-text', 'border-input-text');
            visualCheckbox.classList.add('border-input-border', 'bg-white');
            
            const checkmark = visualCheckbox.querySelector('svg');
            if (checkmark) {
              checkmark.classList.remove('opacity-100');
              checkmark.classList.add('opacity-0');
            }
          }
        }
      });
      
      // Clear all price range inputs
      document.querySelectorAll('input[name*="filter.v.price"]').forEach(input => {
        input.value = '';
      });
      
      // Clear sorting
      const sortSelect = form.querySelector('select[name="sort_by"]');
      if (sortSelect) {
        sortSelect.value = '';
      }
      
      // Apply the cleared filters directly without triggering form submission
      this.#applyMobileFilters();
      
      // Force visual update after everything has processed
      setTimeout(() => {
        document.querySelectorAll('input[type="checkbox"][name^="filter."]').forEach(checkbox => {
          checkbox.checked = false;
          
          const label = checkbox.closest('label');
          if (label) {
            const visualCheckbox = label.querySelector('span.h-4.w-4');
            if (visualCheckbox) {
              visualCheckbox.classList.remove('bg-input-text', 'border-input-text');
              visualCheckbox.classList.add('border-input-border', 'bg-white');
              
              const checkmark = visualCheckbox.querySelector('svg');
              if (checkmark) {
                checkmark.classList.remove('opacity-100');
                checkmark.classList.add('opacity-0');
              }
            }
          }
        });
      }, 50);
    }
  }

  #applyMobileFilters = () => {
    // Get the main filter form from the page (NOT the mobile one)
    // Find the facet-filters-form that's NOT inside the mobile drawer
    const allFilterForms = document.querySelectorAll('facet-filters-form');
    let desktopFilterForm = null;
    
    allFilterForms.forEach(formElement => {
      // Skip if it's inside the mobile drawer
      if (!formElement.closest('mobile-filter-drawer-content')) {
        desktopFilterForm = formElement.querySelector('form');
      }
    });
    
    const mainFilterForm = desktopFilterForm;
    
    if (mainFilterForm && mainFilterForm.id !== 'mobile-filter-form') {
      // Copy all form data from mobile form to main form
      const mobileForm = document.getElementById('mobile-filter-form');
      const mobileFormData = new FormData(mobileForm);
      
      // Clear the main form first
      const mainCheckboxes = mainFilterForm.querySelectorAll('input[type="checkbox"]');
      mainCheckboxes.forEach(checkbox => {
        checkbox.checked = false;
      });
      
      const mainNumberInputs = mainFilterForm.querySelectorAll('input[type="number"]');
      mainNumberInputs.forEach(input => {
        input.value = '';
      });
      
      // Clear sorting
      const mainSortSelect = document.querySelector('select[name="sort_by"]');
      if (mainSortSelect) {
        mainSortSelect.value = '';
      }
      
      // Apply the mobile form data to the main form
      for (const [name, value] of mobileFormData.entries()) {
        if (name === 'sort_by') {
          // Handle sorting select
          const mainSortSelect = document.querySelector('select[name="sort_by"]');
          if (mainSortSelect) {
            mainSortSelect.value = value;
          }
        } else {
          const mainInput = mainFilterForm.querySelector(`[name="${name}"][value="${value}"]`);
          if (mainInput) {
            mainInput.checked = true;
          } else {
            // Handle number inputs
            const mainNumberInput = mainFilterForm.querySelector(`[name="${name}"]`);
            if (mainNumberInput && mainNumberInput.type === 'number') {
              mainNumberInput.value = value;
            }
          }
        }
      }
      
      // Find the desktop facet-filters-form component and trigger its filter change method
      const desktopFacetForm = mainFilterForm.closest('facet-filters-form');
      if (desktopFacetForm && typeof desktopFacetForm.triggerFilterChange === 'function') {
        // Directly call the triggerFilterChange method
        desktopFacetForm.triggerFilterChange();
      } else {
        // Fallback: dispatch change event
        const firstInput = mainFilterForm.querySelector('input, select');
        if (firstInput) {
          firstInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
    
    // Close the drawer
    this.#handleClose();
  }

  #syncOverlayState() {
    const prevTransition = this.style.transition;
    this.style.transition = 'none';
    if (this.isOpen) {
      this.classList.add('translate-x-0');
      this.classList.remove('translate-x-full');
    } else {
      this.classList.remove('translate-x-0');
      this.classList.add('translate-x-full');
    }
    void this.offsetHeight;
    this.style.transition = prevTransition;
  }
}

function initHeaderOverlay() {
  const allDetails = document.querySelectorAll('header details');
  const overlay = document.querySelector('[data-header-overlay]');
  const header = document.querySelector('header');
  const menuInteraction = header?.getAttribute('data-menu-interaction') || 'hover';

  if (menuInteraction === 'hover') {
    // Find all mega menu dropdowns (they're siblings, not children of details)
    const allMegaMenus = document.querySelectorAll('[data-mega-menu]');
    let isHoveringMenu = false;
    let closeTimer = null;

    const openMenu = (details) => {
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }

      const previouslyOpenMenus = Array.from(allDetails).filter(d => 
        d !== details && d.hasAttribute('open')
      );
      
      allDetails.forEach(other => {
        if (other !== details) other.removeAttribute('open');
      });
      
      previouslyOpenMenus.forEach(() => {
        ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayHide));
      });

      if (!details.hasAttribute('open')) {
        details.setAttribute('open', '');
        ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayShow));
      }
    };

    const startCloseTimer = () => {
      if (closeTimer) clearTimeout(closeTimer);
      
      closeTimer = setTimeout(() => {
        if (!isHoveringMenu) {
          allDetails.forEach(d => d.removeAttribute('open'));
          ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayHide));
        }
        closeTimer = null;
      }, 200);
    };

    allDetails.forEach(details => {
      const summary = details.querySelector('summary');
      if (!summary) return;

      details.addEventListener('mouseenter', () => {
        isHoveringMenu = true;
        openMenu(details);
      });

      details.addEventListener('mouseleave', () => {
        isHoveringMenu = false;
        startCloseTimer();
      });

      summary.addEventListener('click', (e) => e.preventDefault());
    });

    // Also track mega menu hover
    allMegaMenus.forEach(megaMenu => {
      megaMenu.addEventListener('mouseenter', () => {
        isHoveringMenu = true;
        if (closeTimer) {
          clearTimeout(closeTimer);
          closeTimer = null;
        }
      });

      megaMenu.addEventListener('mouseleave', () => {
        isHoveringMenu = false;
        startCloseTimer();
      });
    });
  } else {
    // Click mode
    allDetails.forEach(details => {
      const summary = details.querySelector('summary');
      if (!summary) return;

      summary.addEventListener('click', () => {
        const previouslyOpenMenus = Array.from(allDetails).filter(d => 
          d !== details && d.hasAttribute('open')
        );
        
        allDetails.forEach(other => {
          if (other !== details) other.removeAttribute('open');
        });
        
        previouslyOpenMenus.forEach(() => {
          ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayHide));
        });

        setTimeout(() => {
          const isOpen = details.hasAttribute('open');
          if (isOpen) {
            ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayShow));
            document.body.classList.add('overflow-hidden');
          } else {
            ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayHide));
            document.body.classList.remove('overflow-hidden');
          }
        }, 10);
      });
    });
  }

  overlay?.addEventListener('click', () => {
    // Count currently open menus before closing them
    const openMenuCount = Array.from(allDetails).filter(d => d.hasAttribute('open')).length;
    
    // Close all open details elements
    allDetails.forEach(details => {
      details.removeAttribute('open');
    });
    
    // Dispatch hide events for each menu that was open
    for (let i = 0; i < openMenuCount; i++) {
      ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayHide));
    }
    
    // Also close predictive search if open
    ThemeBus.dispatchEvent(new Event(EVENTS.predictiveSearchClose));
    
    document.body.classList.remove('overflow-hidden');
  });
}

/**
 * When transparent header is active: add menu-open on hover and when any overlay opens
 * (mega menu, cart drawer, header drawer, search/modal). Uses ref count so we only
 * remove menu-open when all are closed. CSS handles transition delay when going back to transparent.
 */
function initHeaderSolidOnInteraction() {
  const header = document.querySelector('header');
  if (!header) return;

  let openCount = 0;

  const addMenuOpen = () => {
    openCount = Math.max(0, openCount) + 1;
    header.classList.add('menu-open');
  };

  const removeMenuOpen = () => {
    openCount = Math.max(0, openCount - 1);
    if (openCount === 0) {
      header.classList.remove('menu-open');
    }
  };

  ThemeBus.addEventListener(EVENTS.headerOverlayShow, addMenuOpen);
  ThemeBus.addEventListener(EVENTS.headerOverlayHide, removeMenuOpen);
  ThemeBus.addEventListener(EVENTS.drawerOverlayShow, addMenuOpen);
  ThemeBus.addEventListener(EVENTS.drawerOverlayHide, removeMenuOpen);
}

/**
 * Transparent header: add class on mouseover so header goes solid (only when "Remove transparency on hover" is enabled)
 * Note: .shopify-section-group-header-group is applied to each section (toolbar, header), not a wrapper – so we listen on all of them.
 */
function initHeaderTransparentHover() {
  const headerGroups = document.querySelectorAll('.shopify-section-group-header-group');
  const header = document.querySelector('header');
  if (!headerGroups.length || !header) return;

  function addHovered() {
    if (header.getAttribute('data-transparency') === 'true') {
      if (header.getAttribute('data-remove-transparency-on-hover') === 'true') {
        header.classList.add('header-hovered');
      }
    } else {
      header.classList.add('header-hovered');
    }
  }

  function removeHovered(e) {
    const related = e.relatedTarget;
    if (!related || !related.closest) return header.classList.remove('header-hovered');
    // Only remove if we're not moving into another header-group section or the header itself
    if (related.closest('.shopify-section-group-header-group') || related.closest('header')) return;
    header.classList.remove('header-hovered');
  }

  headerGroups.forEach((el) => {
    el.addEventListener('mouseenter', addHovered);
    el.addEventListener('mouseleave', removeHovered);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initHeaderOverlay();
  initHeaderSolidOnInteraction();
  initHeaderTransparentHover();
});


document.addEventListener("DOMContentLoaded", () => {
  if (!customElements.get("cart-drawer-component")) {
    customElements.define("cart-drawer-component", CartDrawerComponent);
  }
  if (!customElements.get("header-drawer-component")) {
    customElements.define("header-drawer-component", HeaderDrawerComponent);
  }
  if (!customElements.get("header-drawer-menu")) {
    customElements.define("header-drawer-menu", HeaderDrawerMenu);
  }
  if (!customElements.get("search-drawer-component")) {
    customElements.define("search-drawer-component", SearchDrawerComponent);
  }
  if (!customElements.get("mobile-filter-drawer-component")) {
    customElements.define("mobile-filter-drawer-component", MobileFilterDrawerComponent);
  }
  // Keep the header cart bubble in sync when cart changes
  ThemeBus.addEventListener(EVENTS.cartUpdated, () => updateCartBubble());
  ThemeBus.addEventListener(EVENTS.cartItemAdded, () => updateCartBubble());
});

// Lazy loading opacity handler
class LazyImageHandler {
  constructor() {
    this.init();
  }

  init() {
    // Handle existing lazy images
    this.handleLazyImages();
    
    // Handle new lazy images (for dynamic content)
    document.addEventListener('shopify:section:load', () => {
      this.handleLazyImages();
    });
  }

  handleLazyImages() {
    const lazyImages = document.querySelectorAll('.content-image img[loading="lazy"]');
    
    lazyImages.forEach(img => {
      // Skip if already processed
      if (img.dataset.lazyProcessed) return;
      
      // Mark as processed
      img.dataset.lazyProcessed = 'true';
      
      // Set initial opacity to 0 for transition
      img.style.opacity = '0';
      img.style.transition = 'opacity 0.3s ease-in-out';
      
      // Handle load event
      img.addEventListener('load', () => {
        img.style.opacity = '1';
      });
      
      // Handle error event
      img.addEventListener('error', () => {
        img.style.opacity = '1'; // Show even if error
      });
      
      // Fallback: if image is already loaded
      if (img.complete && img.naturalHeight !== 0) {
        img.style.opacity = '1';
      }
    });
  }
}

// Initialize lazy image handler
document.addEventListener('DOMContentLoaded', () => {
  new LazyImageHandler();
  initHeaderOverlayController();
  initDrawerOverlayController();
});


// Export for global access
window.LazyImageHandler = LazyImageHandler;

// Sync mobile filter drawer form with current URL (centralized)
function updateMobileFilters() {
  const mobileForm = document.getElementById('mobile-filter-form');
  if (!mobileForm) return;

  const urlParams = new URLSearchParams(window.location.search);

  // Get the active faceted filter to skip (if on a sub-collection)
  // This filter is in the URL path, not params, so we shouldn't uncheck it
  let facetedFilterParam = null;
  let facetedFilterValue = null;
  const facetedOption = mobileForm.querySelector('[data-faceted-active-filter-option]');
  if (facetedOption) {
    facetedFilterParam = facetedOption.getAttribute('data-filter-param');
    facetedFilterValue = facetedOption.getAttribute('data-value');
  }

  mobileForm.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
    const paramName = checkbox.name;
    const paramValue = checkbox.value;
    
    // Skip the active faceted filter - it's controlled by the sub-collection URL, not params
    if (facetedFilterParam && paramName === facetedFilterParam && paramValue === facetedFilterValue) {
      return;
    }
    
    // All other filters: sync from URL params
    const allValues = urlParams.getAll(paramName);
    checkbox.checked = allValues.includes(paramValue);
  });

  mobileForm.querySelectorAll('input[type="number"]').forEach((input) => {
    const paramName = input.name;
    const paramValue = urlParams.get(paramName);
    input.value = paramValue || '';
  });

  const mobileSortSelect = mobileForm.querySelector('select[name="sort_by"]');
  if (mobileSortSelect) {
    const sortValue = urlParams.get('sort_by');
    mobileSortSelect.value = sortValue || '';
  }
}

document.addEventListener('DOMContentLoaded', updateMobileFilters);
ThemeBus.addEventListener(EVENTS.filtersUpdated, updateMobileFilters);

/**
 * Account dropdown: delegated handlers for plain markup (snippet account-selector).
 * Scoped with [data-account-selector] so desktop + drawer each toggle their own panel.
 * The <account-modal> custom element is optional; theme markup does not use it by default.
 */
document.addEventListener('DOMContentLoaded', () => {
  const openAccountModal = (modal) => {
    document.querySelectorAll('[data-account-modal]').forEach((m) => {
      if (m !== modal && !m.classList.contains('hidden')) {
        closeAccountModal(m);
      }
    });
    modal.classList.remove('hidden');
    modal.classList.add('opacity-0');
    requestAnimationFrame(() => {
      modal.classList.remove('opacity-0');
      modal.classList.add('opacity-100');
    });
    document.body.classList.add('overflow-hidden');
    ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayShow));
  };

  const closeAccountModal = (modal) => {
    const onEnd = () => {
      modal.classList.add('hidden');
      modal.removeEventListener('transitionend', onEnd);
    };
    modal.addEventListener('transitionend', onEnd);
    modal.classList.remove('opacity-100');
    modal.classList.add('opacity-0');
    document.body.classList.remove('overflow-hidden');
    ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayHide));
  };

  document.addEventListener('click', (e) => {
    const toggleEl = e.target.closest('[data-account-modal-toggle]');
    if (toggleEl) {
      e.preventDefault();
      const root = toggleEl.closest('[data-account-selector]');
      const modal = root?.querySelector('[data-account-modal]');
      if (!modal) return;

      if (modal.classList.contains('hidden')) {
        openAccountModal(modal);
      } else {
        closeAccountModal(modal);
      }
      return;
    }

    if (e.target.closest('[data-account-modal]')) return;

    document.querySelectorAll('[data-account-modal]').forEach((modal) => {
      if (!modal.classList.contains('hidden')) {
        closeAccountModal(modal);
      }
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('[data-account-modal]').forEach((modal) => {
      if (!modal.classList.contains('hidden')) {
        closeAccountModal(modal);
      }
    });
  });
});

// Account modal component (follows LocalizationForm pattern)
class AccountModal extends Component {
  constructor() {
    super();
    this.elements = {
      button: this.querySelector('[data-account-modal-toggle]'),
      panel: this.querySelector('[data-account-modal]'),
    };

    this.elements.button.addEventListener('click', this.openSelector.bind(this));
    this.elements.button.addEventListener('focusout', this.closeSelector.bind(this));
    this.addEventListener('keyup', this.onContainerKeyUp.bind(this));

    // Close dropdown when clicking outside
    document.addEventListener('click', this.onDocumentClick.bind(this));
  }

  hidePanel() {
    // Only trigger event if panel is actually visible (going from open to closed)
    const wasVisible = !this.elements.panel.classList.contains('pointer-events-none');
    
    this.elements.button.setAttribute('aria-expanded', 'false');
    this.elements.panel.classList.remove('opacity-100', 'translate-y-0', 'scale-100', 'pointer-events-auto');
    this.elements.panel.classList.add('opacity-0', '-translate-y-[5px]', 'scale-95', 'pointer-events-none');
    document.body.classList.remove('overflow-hidden');
    
    // Only dispatch closed event if panel was actually visible
    if (wasVisible) {
      ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayHide));
    }
  }

  showPanel() {
    this.elements.button.setAttribute('aria-expanded', 'true');
    this.elements.panel.classList.remove('opacity-0', '-translate-y-[5px]', 'scale-95', 'pointer-events-none');
    this.elements.panel.classList.add('opacity-100', 'translate-y-0', 'scale-100', 'pointer-events-auto');
    document.body.classList.add('overflow-hidden');
    ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayShow));
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    this.hidePanel();
    this.elements.button.focus();
  }

  openSelector() {
    this.elements.button.focus();
    const isExpanded = this.elements.button.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      this.hidePanel();
    } else {
      this.showPanel();
    }
  }

  closeSelector(event) {
    const shouldClose = event.relatedTarget && !this.contains(event.relatedTarget);
    if (event.relatedTarget === null || shouldClose) {
      this.hidePanel();
    }
  }

  onDocumentClick(event) {
    // Only close if clicking outside and panel is visible
    const isVisible = !this.elements.panel.classList.contains('pointer-events-none');
    if (isVisible && !this.contains(event.target)) {
      this.hidePanel();
    }
  }
}

class LocalizationForm extends Component {
  constructor() {
    super();
    this.elements = {
      input: this.querySelector('input[name="country_code"]'),
      button: this.querySelector('.localization-selector__button'),
      panel: this.querySelector('.localization-selector__list'),
    };

    this.elements.button.addEventListener('click', this.openSelector.bind(this));
    this.elements.button.addEventListener('focusout', this.closeSelector.bind(this));
    this.addEventListener('keyup', this.onContainerKeyUp.bind(this));

    this.querySelectorAll('a').forEach(item => item.addEventListener('click', this.onItemClick.bind(this)));

    // Close dropdown when clicking outside
    document.addEventListener('click', this.onDocumentClick.bind(this));
  }

  hidePanel() {
    // Only trigger event if panel is actually visible (going from open to closed)
    const wasVisible = !this.elements.panel.classList.contains('pointer-events-none');
    
    this.elements.button.setAttribute('aria-expanded', 'false');
    this.elements.panel.classList.remove('opacity-100', 'translate-y-0', 'scale-100', 'pointer-events-auto');
    this.elements.panel.classList.add('opacity-0', '-translate-y-[5px]', 'scale-95', 'pointer-events-none');
    
    // Only dispatch closed event if panel was actually visible
    if (wasVisible) {
      ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayHide));
    }
    // Legacy header:setInactive removed in favor of ThemeBus overlay events
  }

  showPanel() {
    this.elements.button.setAttribute('aria-expanded', 'true');
    this.elements.panel.classList.remove('opacity-0', '-translate-y-[5px]', 'scale-95', 'pointer-events-none');
    this.elements.panel.classList.add('opacity-100', 'translate-y-0', 'scale-100', 'pointer-events-auto');
    ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayShow));
    // Legacy header:setActive removed in favor of ThemeBus overlay events
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    this.hidePanel();
    this.elements.button.focus();
  }

  onItemClick(event) {
    event.preventDefault();
    const form = this.querySelector('form');
    this.elements.input.value = event.currentTarget.dataset.value;
    if (form) form.submit();
  }

  openSelector() {
    this.elements.button.focus();
    const isExpanded = this.elements.button.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      this.hidePanel();
    } else {
      this.showPanel();
    }
  }

  closeSelector(event) {
    const shouldClose = event.relatedTarget && !this.contains(event.relatedTarget);
    if (event.relatedTarget === null || shouldClose) {
      this.hidePanel();
    }
  }

  onDocumentClick(event) {
    // Only close if clicking outside and panel is visible
    const isVisible = !this.elements.panel.classList.contains('pointer-events-none');
    if (isVisible && !this.contains(event.target)) {
      this.hidePanel();
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  if (!customElements.get("localization-form")) {
    customElements.define("localization-form", LocalizationForm);
  }
  if (!customElements.get("account-modal")) {
    customElements.define("account-modal", AccountModal);
  }
  // Inventory status updater using ThemeBus variant events
  try {
    const updateInventoryStatus = (max, outOfStock) => {
      const qty = Number.isFinite(max) ? Number(max) : max; // Infinity stays Infinity
      const showOut = !!outOfStock || qty <= 0;
      const showLow = !showOut && qty > 0 && qty <= 5;
      const showIn = !showOut && !showLow && qty > 0;

      document.querySelectorAll('inventory-status').forEach((el) => {
        const inEl = el.querySelector('.inventory-status-in-stock');
        const lowEl = el.querySelector('.inventory-status-low-stock');
        const outEl = el.querySelector('.inventory-status-out-of-stock');
        if (inEl) inEl.classList.toggle('hidden', !showIn);
        if (lowEl) lowEl.classList.toggle('hidden', !showLow);
        if (outEl) outEl.classList.toggle('hidden', !showOut);
      });
    };
    ThemeBus.addEventListener(EVENTS.variantInventory, (e) => {
      const max = Number(e.detail?.max ?? 0);
      updateInventoryStatus(max, false);
    });
    ThemeBus.addEventListener(EVENTS.variantOutOfStock, () => {
      updateInventoryStatus(0, true);
    });
  } catch (_) {}
  
  // Search icon click handler
  try {
    const searchIconButtons = document.querySelectorAll('[ref="searchModalToggle"]');
    searchIconButtons.forEach(button => {
      button.addEventListener('click', () => {
        ThemeBus.dispatchEvent(new Event(EVENTS.searchDrawerOpen));
      });
    });
  } catch (_) {}
});